import { AttInfoT, AttWeeklyInfoT } from 'types/shared/core/Attendance';
import TimeStr from 'utils/shared/TimeStr';

type LessonWeeklyInfoOwnerT = {
  lessonWeeklyInfo?: AttWeeklyInfoT | null;
  lessonWeeklyPackedInfo?: string | null;
};

class WeeklyScheduleInfoHelper {
  private static readonly summaryDayOrder = [1, 2, 3, 4, 5, 6, 0];
  private static readonly summaryDayLabelMap = new Map<number, string>([
    [1, '월'],
    [2, '화'],
    [3, '수'],
    [4, '목'],
    [5, '금'],
    [6, '토'],
    [0, '일'],
  ]);

  private static readonly daySearchAliasMap = new Map<number, string[]>([
    [1, ['월', '월요', '월요일']],
    [2, ['화', '화요', '화요일']],
    [3, ['수', '수요', '수요일']],
    [4, ['목', '목요', '목요일']],
    [5, ['금', '금요', '금요일']],
    [6, ['토', '토요', '토요일']],
    [0, ['일', '일요', '일요일']],
  ]);

  static validateTime = (value: string): boolean => /^(2[0-3]|[01][0-9])[0-5][0-9]$/.test(value);

  static packDay(info: AttInfoT): string {
    const pt = info.presentTime.padStart(4, '0').slice(-4);
    const lt = info.leftTime.padStart(4, '0').slice(-4);

    const mark = (info.mark ?? '').replace(/[|;]/g, '');
    const teacherIdStr = info.teacherId && info.teacherId !== 0 ? String(info.teacherId) : '';

    return (
      String(info.dayOfWeek) + (info.hasLesson ? '1' : '0') + pt + lt + mark + ';' + teacherIdStr
    );
  }

  static pack(week: AttWeeklyInfoT): string {
    return week.map(WeeklyScheduleInfoHelper.packDay).join('|');
  }

  static unpackDay(seg: string): AttInfoT | null {
    if (seg.length < 10) return null;

    const dowChar = seg[0];
    const flagChar = seg[1];

    const dayOfWeek = parseInt(dowChar, 10);
    if (Number.isNaN(dayOfWeek) || dayOfWeek < 0 || dayOfWeek > 6) return null;
    if (flagChar !== '0' && flagChar !== '1') return null;

    let pt = seg.slice(2, 6);
    let lt = seg.slice(6, 10);

    if (!this.validateTime(pt) || !this.validateTime(lt)) return null;

    if (pt === '0000') pt = '';
    if (lt === '0000') lt = '';

    const rest = seg.slice(10);
    const semiIdx = rest.indexOf(';');

    let mark = rest;
    let teacherId = 0;
    if (semiIdx !== -1) {
      mark = rest.slice(0, semiIdx);
      const tidStr = rest.slice(semiIdx + 1);
      if (tidStr !== '') {
        const parsed = parseInt(tidStr, 10);
        teacherId = Number.isNaN(parsed) ? 0 : parsed;
      }
    }

    return {
      dayOfWeek,
      hasLesson: flagChar === '1',
      presentTime: pt,
      leftTime: lt,
      mark,
      teacherId,
    };
  }

  static unpack(str: string | null): AttWeeklyInfoT | null {
    if (!str) return [];

    const segs = str.split('|');
    const week: AttWeeklyInfoT = [];

    for (const seg of segs) {
      if (seg === '') continue;
      const dayInfo = this.unpackDay(seg);
      if (dayInfo === null) return null;
      week.push(dayInfo);
    }

    return week;
  }

  static adHocTeacherKey(studentId: number, presentTime: string) {
    return `${studentId}-${presentTime}`;
  }

  private static formatSummaryTimeRange(presentTime: string, leftTime: string) {
    const start = TimeStr.readableTime(presentTime);
    const end = TimeStr.readableTime(leftTime);

    if (start && end) return `${start}~${end}`;
    if (start) return start;
    if (end) return `~${end}`;
    return '';
  }

  private static summarizeDaySet(daySet: Set<number>) {
    const sortedDays = this.summaryDayOrder.filter((dayOfWeek) => daySet.has(dayOfWeek));
    const key = sortedDays.join(',');

    if (key === '1,2,3,4,5') return '주중';
    if (key === '1,2,3,4,5,6') return '주중·토';
    if (key === '1,2,3,4,5,6,0') return '매일';

    return sortedDays.map((dayOfWeek) => this.summaryDayLabelMap.get(dayOfWeek) || '').join('');
  }

  static summarizeLessonWeeklyInfo(week: AttWeeklyInfoT | null | undefined): string {
    if (!week?.length) return '';

    const groupedDaySetMap = new Map<string, Set<number>>();

    for (const info of week) {
      if (!info.hasLesson || !this.validateTime(info.presentTime)) continue;

      const key = `${info.presentTime}|${info.leftTime || ''}`;
      const daySet = groupedDaySetMap.get(key) || new Set<number>();
      daySet.add(info.dayOfWeek);
      groupedDaySetMap.set(key, daySet);
    }

    return Array.from(groupedDaySetMap.entries())
      .map(([timeKey, daySet]) => {
        const [presentTime, leftTime] = timeKey.split('|');
        return {
          daySummary: this.summarizeDaySet(daySet),
          dayOrder:
            Math.min(
              ...this.summaryDayOrder
                .map((dayOfWeek, index) => (daySet.has(dayOfWeek) ? index : Infinity))
                .filter(Number.isFinite),
            ) || 0,
          presentTime,
          leftTime,
          text: `${this.summarizeDaySet(daySet)}${this.formatSummaryTimeRange(
            presentTime,
            leftTime,
          )}`,
        };
      })
      .filter((item) => item.daySummary)
      .sort((a, b) => {
        if (a.dayOrder !== b.dayOrder) return a.dayOrder - b.dayOrder;
        if (a.presentTime !== b.presentTime) return a.presentTime.localeCompare(b.presentTime);
        return a.leftTime.localeCompare(b.leftTime);
      })
      .map((item) => item.text)
      .join(', ');
  }

  static summarizeClsLessonSchedule(cls: LessonWeeklyInfoOwnerT): string {
    const lessonWeeklyInfo =
      cls.lessonWeeklyInfo ?? WeeklyScheduleInfoHelper.unpack(cls.lessonWeeklyPackedInfo || null);

    return WeeklyScheduleInfoHelper.summarizeLessonWeeklyInfo(lessonWeeklyInfo);
  }

  static getLessonDaySearchKeywords(week: AttWeeklyInfoT | null | undefined): string[] {
    if (!week?.length) return [];

    const keywordSet = new Set<string>();

    for (const info of week) {
      if (!info.hasLesson) continue;

      const aliases = this.daySearchAliasMap.get(info.dayOfWeek) || [];
      aliases.forEach((alias) => keywordSet.add(alias));
    }

    return Array.from(keywordSet);
  }

  static getClsLessonDaySearchKeywords(cls: LessonWeeklyInfoOwnerT): string[] {
    const lessonWeeklyInfo =
      cls.lessonWeeklyInfo ?? WeeklyScheduleInfoHelper.unpack(cls.lessonWeeklyPackedInfo || null);

    return this.getLessonDaySearchKeywords(lessonWeeklyInfo);
  }

  static matchesClsLessonDayKeyword(cls: LessonWeeklyInfoOwnerT, keyword: string): boolean {
    const normalizedKeyword = keyword.trim();
    if (!normalizedKeyword) return false;

    return this.getClsLessonDaySearchKeywords(cls).includes(normalizedKeyword);
  }
}

export default WeeklyScheduleInfoHelper;
